import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../../../../../libs/core';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CrmAuthApiService } from './crm-auth-api.service';
import { CrmAuthCookieService } from './crm-auth-cookie.service';
import { CrmAuthFlowService } from './crm-auth-flow.service';
import { CrmOnboardingAccessService } from './crm-onboarding-access.service';
import { CrmPermissionStateService } from './crm-permission-state.service';

@Injectable({ providedIn: 'root' })
export class CrmAuthSessionService {
  private readonly lastProtectedRouteKey = 'crm.last-protected-route';
  private readonly defaultProtectedRoute = '/dashboard';
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly crmAuthApiService = inject(CrmAuthApiService);
  private readonly crmAuthCookieService = inject(CrmAuthCookieService);
  private readonly crmAuthFlowService = inject(CrmAuthFlowService);
  private readonly crmOnboardingAccessService = inject(CrmOnboardingAccessService);
  private readonly crmPermissionStateService = inject(CrmPermissionStateService);

  private logoutRequestInFlight = false;

  isAuthenticated(cleanupOnMismatch = false): boolean {
    const hasCookies = this.crmAuthCookieService.hasAuthCookies();
    let hasAppSession = this.authService.isAuthenticated();

    // Refresh-safe rehydrate: cookies may exist before in-memory/core session is available.
    if (hasCookies && !hasAppSession) {
      this.authService.hydrate();
      hasAppSession = this.authService.isAuthenticated();
    }

    if (hasCookies && hasAppSession) {
      return true;
    }

    if (cleanupOnMismatch) {
      this.clearSessionArtifacts();
    }

    return false;
  }

  setLastProtectedRoute(url: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.isProtectedRoute(url)) {
      return;
    }

    localStorage.setItem(this.lastProtectedRouteKey, url);
  }

  getLastProtectedRoute(): string {
    if (typeof window === 'undefined') {
      return this.defaultProtectedRoute;
    }

    const saved = localStorage.getItem(this.lastProtectedRouteKey);
    if (!saved || !this.isProtectedRoute(saved)) {
      return this.defaultProtectedRoute;
    }

    return saved;
  }

  clearLastProtectedRoute(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(this.lastProtectedRouteKey);
  }

  logout(): void {
    if (this.logoutRequestInFlight) {
      return;
    }

    const refreshToken = this.crmAuthCookieService.getRefreshToken();
    this.logoutRequestInFlight = true;

    if (!refreshToken) {
      this.logoutRequestInFlight = false;
      this.clearSessionArtifacts();
      void this.router.navigateByUrl('/login', { replaceUrl: true });
      return;
    }

    this.crmAuthApiService
      .logout({ refreshToken })
      .pipe(
        map(() => null),
        catchError(() => of(null)),
        finalize(() => {
          this.logoutRequestInFlight = false;
          this.clearSessionArtifacts();
          void this.router.navigateByUrl('/login', { replaceUrl: true });
        }),
      )
      .subscribe();
  }

  private clearSessionArtifacts(): void {
    this.crmAuthCookieService.clearAuthCookies();
    this.crmAuthFlowService.clearChallengeContext();
    this.crmAuthFlowService.clearPersistLogin();
    this.crmOnboardingAccessService.clear();
    this.crmPermissionStateService.clear();
    this.clearLastProtectedRoute();
    this.authService.logout();
  }

  isProtectedRoute(url: string): boolean {
    const path = `/${String(url || '').replace(/^\/+/, '')}`;

    if (path === '/' || path === '') {
      return false;
    }

    const disallowedPrefixes = [
      '/login',
      '/verify',
      '/forgot-password',
      '/set-password',
      '/reset-password',
      '/error',
      '/manual-tenant',
      '/create-hq',
      '/application-under-review',
      '/in-review-thank-you',
    ];

    return !disallowedPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
  }
}